/**
 * AdminProfile - Admin account settings (change email, password, name)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { User, Mail, Lock, Save, Loader2, Shield, CheckCircle } from 'lucide-react';
import api, { authAPI, getErrorMessage } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts';

const AdminProfile = () => {
  const queryClient = useQueryClient();
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Fetch current profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ['admin-profile'],
    queryFn: () => authAPI.getAdminProfile().then(r => r.data),
  });

  // Update form when profile loads
  React.useEffect(() => {
    if (profile && !profileLoaded) {
      setProfileForm({
        name: profile.name || profile.first_name || '',
        email: profile.email || ''
      });
      setProfileLoaded(true);
    }
  }, [profile, profileLoaded]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data) => api.put('/admin/profile', data),
    onSuccess: (response) => {
      toast.success('Profile updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['admin-profile'] });
      // Update form with new values
      if (response.data) {
        setProfileForm({
          name: response.data.name || '',
          email: response.data.email || ''
        });
      }
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update profile'))
  });

  // Update password mutation
  const updatePasswordMutation = useMutation({
    mutationFn: (data) => api.put('/admin/password', data),
    onSuccess: () => {
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to change password'))
  });

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileForm);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    updatePasswordMutation.mutate({
      current_password: passwordForm.current_password,
      new_password: passwordForm.new_password
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <DashboardLayout title="Profile Settings" showBackButton>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Account Settings</h2>
          <p className="text-slate-500 mt-1">Manage your admin profile and security settings</p>
        </div>

      {/* Current Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-lg font-semibold">
              {profile?.name?.[0] || profile?.first_name?.[0] || 'A'}
            </div>
            <div>
              <CardTitle className="text-lg">{profile?.name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Admin'}</CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Mail className="h-3 w-3" />
                {profile?.email}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm">
            <Shield className="h-4 w-4 text-indigo-500" />
            <span className="text-slate-600">Role:</span>
            <span className="font-medium capitalize">{profile?.role?.replace('_', ' ') || 'Admin'}</span>
            {profile?.is_super_admin && (
              <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                Super Admin
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Update Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Update Profile
          </CardTitle>
          <CardDescription>Change your name and email address</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                placeholder="Enter your name"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                placeholder="Enter your email"
                data-testid="profile-email-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={updateProfileMutation.isLoading}
              data-testid="save-profile-btn"
            >
              {updateProfileMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              variant="outline"
              disabled={updatePasswordMutation.isLoading || !passwordForm.current_password || !passwordForm.new_password}
              data-testid="change-password-btn"
            >
              {updatePasswordMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      <Alert>
        <CheckCircle className="h-4 w-4" />
        <AlertDescription>
          After changing your email, you'll need to use the new email to log in.
        </AlertDescription>
      </Alert>
      </div>
    </DashboardLayout>
  );
};

export default AdminProfile;
